import NeptuneElement from '../../NeptuneElement.js';

class Checkbox extends NeptuneElement {

    templateMarkup = `
        <div>
            <div>
                <input type="checkbox" />
                <label></label>
            </div>
            <div>
                <span></span>
                <ul></ul>
            </div>
        </div>
    `;

    componentCssUrl = '/src/components/form/checkbox/checkbox.css';

    static formAssociated = true;

    #internals = null;

    /**
     * Creates a new instance of the Checkbox component, initializing the ElementInternals for form association.
     */
    constructor() {
        super();
        this.#internals = this.attachInternals();
    }

    /**
     * Lifecycle method called when the component is added to the DOM. It processes initial attributes, sets up accessibility features, and attaches event listeners.
     * @returns {Promise<void>}
      * @throws "neptune-checkbox: "name" attribute is required." if the "name" attribute is not provided.
     */
    async connectedCallback() {
        await super.connectedCallback();
        
        if (!this.hasAttribute('name')) {
            throw new Error('neptune-checkbox: "name" attribute is required.');
        }
        
        this.processInitialAttributesAfterRender(Checkbox.observedAttributes);
        this.setupAccessibility();
        this.setEventListeners();
    }

    /**
     * Cleanup method called when the component is removed from the DOM. It removes event listeners to prevent memory leaks.
     * @returns {Promise<void>}
     */
    async disconnectedCallback() {
        this.removeEventListeners();
        await super.disconnectedCallback();
    }

    /**
     * Lists the attributes to observe for changes. When any of these attributes change, the `attributeChangedCallback` will be triggered to update the component's state accordingly.
     * @returns {string[]} An array of attribute names to observe.
     */
    static get observedAttributes() {
        return ['name', 'value', 'checked', 'disabled', 'label', 'indeterminate', 'required', 'autocomplete', 'error', 'error-text', 'supporting-text'];
    }

    /**
     * Responds to changes in observed attributes by updating the corresponding properties of the checkbox input and related elements. It also handles form value updates and accessibility attributes based on the current state of the component.
     * @param {string} name - The name of the attribute that changed.
     * @param {string|null} oldValue - The previous value of the attribute before the change.
     * @param {string|null} newValue - The new value of the attribute after the change.
     */
    attributeChangedCallback(name, oldValue, newValue) {
        
        if (!this.isRendered) {
            return;
        }
        
        this.processAttribute(name, oldValue, newValue);
    }   

    /**
     * Sets up accessibility features for the checkbox component by associating the input with its label and any error or supporting text. It generates unique IDs for these elements and uses ARIA attributes to ensure that assistive technologies can correctly identify and describe the component's state and any associated messages.
     * @returns {void}
     */
    setupAccessibility() {
        const checkboxInput = this.shadowRoot.querySelector('input[type="checkbox"]');
        const label = this.shadowRoot.querySelector('label');
        const errorList = this.shadowRoot.querySelector('ul');
        const supportingText = this.shadowRoot.querySelector('span');
        
        if (!checkboxInput || !label) {return;}

        const uniqueId = `neptune-checkbox-${Math.random().toString(36).substr(2, 9)}`;
        
        checkboxInput.id = uniqueId;
        label.setAttribute('for', uniqueId);

        if (errorList) {
            errorList.id = `neptune-checkbox-errors-${Math.random().toString(36).substr(2, 9)}`;
        }

        if (supportingText) {
            supportingText.id = `neptune-checkbox-support-${Math.random().toString(36).substr(2, 9)}`;
        }

        const describedBy = [errorList?.id, supportingText?.id].filter(Boolean).join(' ');
        if (describedBy) {
            checkboxInput.setAttribute('aria-describedby', describedBy);
        }
    }

    /**
     * Updates the error and supporting messages for the checkbox component based on the current attributes.
     * It sets the appropriate ARIA attributes to ensure that assistive technologies can correctly identify and describe the component's state and any associated messages.
     * @returns {void}
     */
    updateMessages() {
        const errorList = this.shadowRoot.querySelector('ul');
        const supportingText = this.shadowRoot.querySelector('span');

        if (!errorList || !supportingText) {
            return;
        }

        errorList.setAttribute('aria-live', 'polite');
        supportingText.setAttribute('aria-live', 'polite');

        if (this.hasAttribute('error')) {
            const errorText = this.getAttribute('error-text') || '';
            const errors = errorText.split('|').filter((item) => item.trim());

            errorList.innerHTML = '';
            if (errors.length > 0) {
                errors.forEach((error) => {
                    const li = document.createElement('li');
                    li.textContent = error.trim();
                    errorList.appendChild(li);
                });
                errorList.style.display = 'block';
            } else {
                errorList.style.display = 'none';
            }

            const supportingTextValue = this.getAttribute('supporting-text') || '';
            if (supportingTextValue) {
                supportingText.textContent = supportingTextValue;
                supportingText.classList.add('visually-hidden-a11y');
                supportingText.style.display = 'block';
            } else {
                supportingText.style.display = 'none';
            }
        } else {
            errorList.innerHTML = '';
            errorList.style.display = 'none';

            const supportingTextValue = this.getAttribute('supporting-text') || '';
            supportingText.textContent = supportingTextValue;
            supportingText.classList.remove('visually-hidden-a11y');
            supportingText.style.display = supportingTextValue ? 'block' : 'none';
        }
    }

    /**
     * Processes attribute changes for the checkbox component and updates the corresponding properties and ARIA attributes.
     * @param {string} name - The name of the attribute that changed.
     * @param {string|null} oldValue - The previous value of the attribute before the change.
     * @param {string|null} newValue - The new value of the attribute after the change.
     * @returns {void}
     */
    processAttribute(name, oldValue, newValue) {
        const checkboxInput = this.shadowRoot.querySelector('input[type="checkbox"]');
        if(!checkboxInput) {
            return;
        }

        switch (name) {
            case 'name': {
                if(newValue) {
                    checkboxInput.name = newValue;
                } else {
                    checkboxInput.removeAttribute('name');
                    throw new Error('neptune-checkbox: "name" attribute is required.');
                }
                break;
            }
            case 'value': {
                if(newValue) {
                    checkboxInput.value = newValue;
                } else {
                    checkboxInput.removeAttribute('value');
                }
                break;
            }
            case 'checked': {
                const isChecked = this.hasAttribute('checked');
                checkboxInput.checked = isChecked;
                this.#internals.setFormValue?.(isChecked ? (this.getAttribute('value') || 'on') : null);
                break;
            }
            case 'disabled': {
                const isDisabled = this.hasAttribute('disabled');
                checkboxInput.disabled = isDisabled;
                break;
            }
            case 'indeterminate': {
                const isIndeterminate = this.hasAttribute('indeterminate');
                checkboxInput.indeterminate = isIndeterminate;
                break;
            }
            case 'required': {
                this.hasAttribute('required')
                    ? checkboxInput.setAttribute('required', '')
                    : checkboxInput.removeAttribute('required');
                break;
            }
            case 'error': {
                this.hasAttribute('error')
                    ? checkboxInput.setAttribute('aria-invalid', 'true')
                    : checkboxInput.removeAttribute('aria-invalid');
                this.updateMessages();
                break;
            }
            case 'error-text':
            case 'supporting-text': {
                this.updateMessages();
                break;
            }
            case 'autocomplete': {
                const autocomplete = this.getAttribute('autocomplete');
                autocomplete !== null 
                    ? checkboxInput.setAttribute('autocomplete', autocomplete) 
                    : checkboxInput.setAttribute('autocomplete', 'off');
                break;
            }
            case 'label': {
                const labelSpan = this.shadowRoot.querySelector('label');
                if(newValue) {
                    labelSpan.textContent = newValue;
                    labelSpan.style.display = 'inline';
                } else {
                    labelSpan.textContent = '';
                    labelSpan.style.display = 'none';
                }
                break;
            }
            default: {
                break;
            }
        }
    }

    /**
     * Registers event listeners for the checkbox input to handle changes in its state. When the checkbox state changes, it updates the "checked" attribute accordingly and dispatches a custom event with the new state and relevant details. If the checkbox is disabled, it prevents any changes and stops event propagation.
     * @returns {void}
     */
    setEventListeners() {
        this.removeEventListeners();
        const checkboxInput = this.shadowRoot.querySelector('input[type="checkbox"]');
        if(!checkboxInput) {
            return;
        }

        this.handleCheckboxChange = (e) => {
            if(this.hasAttribute('disabled')) {
                e.preventDefault();
                e.stopImmediatePropagation();
                return false;
            }

            if(checkboxInput.checked) {
                this.setAttribute('checked', '');
            } else {
                this.removeAttribute('checked');
            }

            if(this.hasAttribute('indeterminate')) {
                this.removeAttribute('indeterminate');
                checkboxInput.indeterminate = false;
            }

            this.dispatchEvent(new CustomEvent('neptune-checkbox-change', {
                bubbles: true,
                composed: true,
                detail: {
                    value: this.getAttribute('value'),
                    name: this.getAttribute('name'),
                    checked: checkboxInput.checked
                }
            }));
        };

        checkboxInput.addEventListener('change', this.handleCheckboxChange);
    }
    
    /**
     * Removes event listeners from the checkbox input to prevent memory leaks and unintended behavior when the component is disconnected from the DOM.
     * @returns {void}
     */
    removeEventListeners() {
        const checkboxInput = this.shadowRoot?.querySelector('input[type="checkbox"]');
        if(checkboxInput){
            checkboxInput.removeEventListener('change', this.handleCheckboxChange);
        }
    }

    /**
     * Resets the checkbox component to its initial state by removing the "checked", "error", and "error-text" attributes.
     * @returns {void}
     */
    formResetCallback() {
        this.removeAttribute('checked');
        this.removeAttribute('error');
        this.removeAttribute('error-text');
    }

    /**
     * Handles the disabled state of the checkbox component by adding or removing the "disabled" attribute.
     * @param {boolean} isDisabled - Indicates whether the checkbox should be disabled.
     * @returns {void}
     */
    formDisabledCallback(isDisabled) {
        if (isDisabled) {
            this.setAttribute('disabled', '');
        } else {
            this.removeAttribute('disabled');
        }
    }

    /**
     * Returns the validity state of the checkbox component.
     * @returns {ValidityState}
     */
    get validity() {
        return this.#internals.validity;
    }

    /**
     * Returns the validation message of the checkbox component.
     * @returns {string}
     */
    get validationMessage() {
        return this.#internals.validationMessage ?? '';
    }

    /**
     * Indicates whether the checkbox component will validate when the form is submitted. This is determined by the presence of the "required" attribute and the validity of the component.
     * @returns {boolean}
     */
    get willValidate() {
        return this.#internals.willValidate ?? false;
    }

    /**
     * Checks the validity of the checkbox component.
     * @returns {boolean}
     */
    checkValidity() {
        return this.#internals.checkValidity?.() ?? true;
    }

    /**
     * Reports the validity of the checkbox component.
     * @returns {boolean}
     */
    reportValidity() {
        return this.#internals.reportValidity?.() ?? true;
    }
}

customElements.define('neptune-checkbox', Checkbox);

export default Checkbox;